import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, DollarSign, FileText, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/shared/Footer";

const PricingPage = () => {
  const navigate = useNavigate();
  
  return (
    <main className="min-h-screen relative overflow-hidden" style={{ background: '#F8FAFC' }}>
      <div className="container-shell py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2.5 px-3.5 py-2.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4"
            style={{ background: '#fff', border: '1px solid #D9E4F2', color: '#416588' }}>
            <span className="w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6,#14B8A6)' }}></span>
            Membership
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#1E293B' }}>Invitation-Only Access</h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#475569' }}>
            Trialeze is intentionally limited to maintain system integrity, data quality, and competitive advantage for members.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
          <div className="rounded-3xl p-8" style={{ background: '#fff', border: '1px solid #D9E4F2', boxShadow: '0 12px 30px rgba(15,23,42,.08)' }}>
            <div className="text-xs font-extrabold uppercase tracking-wider mb-2" style={{ color: '#57759C' }}>One-time</div>
            <h3 className="text-3xl font-bold mb-2" style={{ color: '#1E293B' }}>$599</h3>
            <p className="text-sm mb-6" style={{ color: '#475569' }}>Activation fee</p>
            <ul className="space-y-3 mb-8" style={{ color: '#475569' }}>
              {['Admin setup', 'Onboarding call', 'System configuration', 'Training session'].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4" style={{ color: '#10B981' }} />{item}
                </li>
              ))}
            </ul>
          </div>
          
          <div className="rounded-3xl p-8 relative" style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)', boxShadow: '0 20px 42px rgba(29,78,216,.25)' }}>
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold" style={{ background: '#10B981', color: '#fff' }}>
              SALE - 44% OFF
            </div>
            <div className="text-xs font-extrabold uppercase tracking-wider mb-2 text-blue-200">Monthly</div>
            <div className="flex items-baseline gap-2 mb-1">
              <h3 className="text-3xl font-bold text-white">$499</h3>
              <span className="text-xl line-through text-blue-300">$899</span>
            </div>
            <p className="text-sm mb-2 text-blue-200">Per month</p>
            <div className="inline-block px-3 py-1 mb-4 rounded-full text-xs font-bold" style={{ background: 'rgba(16,185,129,0.2)', color: '#6EE7B7' }}>
              Limited time offer - next 2 months only
            </div>
            <ul className="space-y-3 mb-8 text-blue-100">
              {['Unlimited case analyses', 'Settlement modeling', 'Judge & attorney profiles', 'Document intelligence', 'Response generation', 'Priority support'].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-300" />{item}
                </li>
              ))}
            </ul>
            <Button onClick={() => navigate('/platform/dashboard')} className="w-full rounded-xl h-12 font-bold bg-white text-blue-600 hover:bg-blue-50">
              Get Started Now
            </Button>
          </div>
          
          <div className="rounded-3xl p-8" style={{ background: '#fff', border: '1px solid #D9E4F2', boxShadow: '0 12px 30px rgba(15,23,42,.08)' }}>
            <div className="text-xs font-extrabold uppercase tracking-wider mb-2" style={{ color: '#57759C' }}>Enterprise</div>
            <h3 className="text-3xl font-bold mb-2" style={{ color: '#1E293B' }}>Custom</h3>
            <p className="text-sm mb-6" style={{ color: '#475569' }}>For large firms</p>
            <ul className="space-y-3 mb-8" style={{ color: '#475569' }}>
              {['Multi-seat access', 'Custom integrations', 'Dedicated support', 'White-label options', 'API access'].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4" style={{ color: '#10B981' }} />{item}
                </li>
              ))}
            </ul>
            <Button variant="outline" className="w-full rounded-xl h-12 font-bold border-slate-300">
              Contact Sales
            </Button>
          </div>
        </div>
        
        {/* Savings Calculator */}
        <div className="rounded-3xl p-8" style={{ background: '#fff', border: '1px solid #D9E4F2', boxShadow: '0 12px 30px rgba(15,23,42,.08)' }}>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#1E293B' }}>ROI Calculator: See Your Potential Savings</h2>
            <p style={{ color: '#475569' }}>Based on average member outcomes and time savings</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { num: '70%', label: 'Research time saved', desc: 'Hours per case', icon: Clock },
              { num: '$15K', label: 'Average settlement increase', desc: 'Per matter', icon: DollarSign },
              { num: '4.2x', label: 'Faster document review', desc: 'vs manual process', icon: FileText },
              { num: '89%', label: 'Member retention', desc: 'Annual renewal rate', icon: TrendingUp }
            ].map(({ num, label, desc, icon: Icon }) => (
              <div key={label} className="text-center p-6 rounded-2xl" style={{ background: 'linear-gradient(180deg,#fff,#f8fbff)', border: '1px solid #D9E4F2' }}>
                <Icon className="h-8 w-8 mx-auto mb-3" style={{ color: '#3B82F6' }} />
                <div className="text-3xl font-extrabold mb-1" style={{ color: '#1E293B' }}>{num}</div>
                <div className="text-sm font-semibold" style={{ color: '#1E293B' }}>{label}</div>
                <div className="text-xs" style={{ color: '#475569' }}>{desc}</div>
              </div>
            ))}
          </div>
          
          {/* Annual Savings Projection */}
          <div className="mt-8 p-6 rounded-2xl" style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)' }}>
            <div className="grid md:grid-cols-3 gap-6 text-white">
              <div>
                <div className="text-sm text-blue-200 mb-1">Monthly Investment</div>
                <div className="flex items-baseline gap-2">
                  <div className="text-2xl font-bold">$499</div>
                  <div className="text-base line-through text-blue-300">$899</div>
                </div>
                <div className="text-xs text-emerald-300 font-semibold">Save $400/mo for 2 months</div>
              </div>
              <div>
                <div className="text-sm text-blue-200 mb-1">Estimated Monthly Value</div>
                <div className="text-2xl font-bold">$8,500+</div>
              </div>
              <div>
                <div className="text-sm text-blue-200 mb-1">Annual ROI</div>
                <div className="text-2xl font-bold">1,600%+</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
};

export default PricingPage;
